import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Minus, Plus, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonStyles } from '@/lib/button-styles'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import PageHero from '@/components/generals/page-hero'
import CtaBand from '@/components/generals/cta-band'
import SmartLink from '@/components/generals/smart-link'
import TubeRender from '@/components/landing/tube-render'
import JarRender from '@/components/landing/jar-render'
import RevealWords from '@/components/landing/reveal-words'

const EASE = [0.22, 1, 0.36, 1] as const

type ProductId = 'tube' | 'jar' | 'duo'

interface Product {
    id: ProductId
    name: string
    size: string
    price: number
    text: string
    tint: string
    badge?: string
}

// Prices are display prices for now. Online checkout is not open yet.
const PRODUCTS: Product[] = [
    {
        id: 'tube',
        name: 'All-Over Balm, tube',
        size: '100 ml',
        price: 14,
        text: "The everyday tube. Slips into a bag or pocket for hands, lips and dry spots on the go.",
        tint: 'bg-blush',
        badge: 'Most loved'
    },
    {
        id: 'jar',
        name: 'All-Over Balm, jar',
        size: '50 ml',
        price: 12,
        text: "A richer scoop for the bedside. Made for knees, elbows and heels overnight.",
        tint: 'bg-sage'
    },
    {
        id: 'duo',
        name: 'The Softly duo',
        size: '100 ml tube + 50 ml jar',
        price: 22,
        text: "One for the day, one for the night. Everything you need for every dry spot.",
        tint: 'bg-butter',
        badge: 'Best value'
    }
]

const formatPrice = (value: number): string => `€${value.toFixed(2)}`

const ProductArt: FC<{ id: ProductId, name: string }> = ({ id, name }): ReactNode => {
    if (id === 'tube') {
        return (
            <div className='aspect-[300/680] h-[74%] -rotate-[12deg] transition-transform duration-1000 ease-out group-hover:-rotate-[6deg]'>
                <TubeRender label={name} details={false} />
            </div>
        )
    }

    if (id === 'jar') {
        return (
            <div className='size-[64%] transition-transform duration-1000 ease-out group-hover:rotate-6'>
                <JarRender lid label={name} />
            </div>
        )
    }

    return (
        <div role='img' aria-label={name} className='relative h-[74%] w-[80%]'>
            <div className='absolute left-[8%] top-0 aspect-[300/680] h-full -rotate-[10deg] transition-transform duration-1000 ease-out group-hover:-rotate-[4deg]'>
                <TubeRender details={false} />
            </div>
            <div className='absolute bottom-[4%] right-0 w-[56%] transition-transform duration-1000 ease-out group-hover:rotate-6'>
                <JarRender />
            </div>
        </div>
    )
}

const Shop: FC = (): ReactNode => {
    const [bag, setBag] = useState<Partial<Record<ProductId, number>>>({})
    const [open, setOpen] = useState(false)

    const items = PRODUCTS.filter(product => (bag[product.id] ?? 0) > 0)
    const count = items.reduce((sum, product) => sum + (bag[product.id] ?? 0), 0)
    const total = items.reduce((sum, product) => sum + product.price * (bag[product.id] ?? 0), 0)

    const change = (id: ProductId, delta: number): void => {
        setBag(current => ({ ...current, [id]: Math.max(0, Math.min(9, (current[id] ?? 0) + delta)) }))
    }

    const add = (id: ProductId): void => {
        change(id, 1)
        setOpen(true)
    }

    return (
        <Layout>
            <Seo
                title='Shop'
                description="Shop Softly All-Over Balm: the everyday tube, the bedside jar and the duo."
            />

            <PageHero
                id='shop-hero'
                label='Shop'
                title='Bring Softly home.'
                intro="One fragrance-free balm in two sizes, or both together. Online checkout opens soon, so for now you can build your bag and join the list to hear first."
                tint='bg-butter'
            />

            <section id='products' aria-labelledby='products-title' className='bg-cream py-20 lg:py-28'>
                <div className='mx-auto max-w-[1440px] px-5 lg:px-10'>
                    <h2 id='products-title' className='sr-only'>Products</h2>

                    <ul className='grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8'>
                        {PRODUCTS.map((product, index) => (
                            <motion.li
                                key={product.id}
                                initial={{ opacity: 0, y: 36 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.25 }}
                                transition={{ duration: 1.1, delay: index * 0.1, ease: EASE }}
                                className='group flex flex-col'
                            >
                                <div className={cn('relative grid aspect-square place-items-center overflow-hidden rounded-[2.5rem]', product.tint)}>
                                    {product.badge && (
                                        <span className='absolute left-5 top-5 rounded-full bg-white px-3 py-1 text-[12px] font-medium text-cocoa'>
                                            {product.badge}
                                        </span>
                                    )}

                                    <ProductArt id={product.id} name={product.name} />
                                </div>

                                <div className='mt-6 flex items-start justify-between gap-4'>
                                    <div>
                                        <h3 className='text-[20px] font-medium text-cocoa'>{product.name}</h3>
                                        <p className='mt-1 text-[14px] text-cocoa/80'>{product.size}</p>
                                    </div>

                                    <p className='text-[20px] font-medium tabular-nums text-cocoa'>{formatPrice(product.price)}</p>
                                </div>

                                <p className='mt-3 text-[16px] leading-relaxed text-cocoa/85'>{product.text}</p>

                                <button
                                    type='button'
                                    onClick={() => add(product.id)}
                                    className={buttonStyles('primary', 'lg', 'mt-6 w-full')}
                                >
                                    Add to bag
                                </button>
                            </motion.li>
                        ))}
                    </ul>
                </div>
            </section>

            <section id='promise' aria-labelledby='promise-title' className='border-t border-cocoa/10 bg-cream py-20 lg:py-28'>
                <div className='mx-auto grid max-w-[1440px] gap-10 px-5 md:grid-cols-3 lg:px-10'>
                    <RevealWords id='promise-title' text='Every tube, kept simple' className='type-display md:col-span-3' />
                    {[
                        ['Fragrance-free', 'Nothing added to scent it, so it suits sensitive noses and skin.'],
                        ['Three ingredients', 'Petrolatum, shea butter and vitamin E. Nothing in the way.'],
                        ['Made for everywhere', 'Hands, knees, elbows, lips and heels, all from one balm.']
                    ].map(([title, text]) => (
                        <div key={title} className='border-t border-cocoa/15 pt-6'>
                            <h3 className='text-[20px] font-medium text-cocoa'>{title}</h3>
                            <p className='mt-2 text-[16px] leading-relaxed text-cocoa/85'>{text}</p>
                        </div>
                    ))}
                </div>
            </section>

            <CtaBand
                id='shop-cta'
                title='Hear the moment checkout opens.'
                text="Join the list and we will let you know as soon as Softly is ready to order."
                primary={{ label: 'Join the list', href: '/#join' }}
                secondary={{ label: 'How it works', href: '/how-it-works' }}
            />

            {/* Bag button, shown once something is in the bag. */}
            <AnimatePresence>
                {count > 0 && !open && (
                    <motion.button
                        type='button'
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.5, ease: EASE }}
                        onClick={() => setOpen(true)}
                        className='fixed bottom-5 right-5 z-40 inline-flex h-12 items-center gap-3 rounded-full bg-cocoa px-5 text-[15px] font-medium text-white shadow-[0_18px_40px_-16px_rgb(74_43_32/0.8)] focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-cocoa'
                    >
                        Your bag
                        <span className='grid size-6 place-items-center rounded-full bg-white text-[13px] text-cocoa'>{count}</span>
                    </motion.button>
                )}
            </AnimatePresence>

            {/* The bag panel. */}
            <AnimatePresence>
                {open && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setOpen(false)}
                            className='fixed inset-0 z-50 bg-cocoa/30 backdrop-blur-[2px]'
                            aria-hidden='true'
                        />

                        <motion.aside
                            role='dialog'
                            aria-modal='true'
                            aria-labelledby='bag-title'
                            data-lenis-prevent
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ duration: 0.7, ease: EASE }}
                            onKeyDown={event => {
                                if (event.key === 'Escape') setOpen(false)
                            }}
                            className='fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col bg-cream shadow-[-30px_0_60px_-30px_rgb(74_43_32/0.5)]'
                        >
                            <div className='flex items-center justify-between border-b border-cocoa/10 px-6 py-5'>
                                <h2 id='bag-title' className='font-heading text-[2.5rem] leading-none tracking-[0.02em] text-cocoa'>Your bag</h2>

                                <button
                                    type='button'
                                    autoFocus
                                    onClick={() => setOpen(false)}
                                    aria-label='Close bag'
                                    className='grid size-10 place-items-center rounded-full border border-cocoa/25 text-cocoa transition-colors duration-500 hover:bg-cocoa hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cocoa'
                                >
                                    <X className='size-4' aria-hidden='true' />
                                </button>
                            </div>

                            <div className='flex-1 overflow-y-auto px-6 py-4'>
                                {items.length === 0 ? (
                                    <p className='py-10 text-center text-[16px] text-cocoa/80'>Your bag is empty.</p>
                                ) : (
                                    <ul>
                                        {items.map(product => (
                                            <li key={product.id} className='flex items-center gap-4 border-b border-cocoa/10 py-4'>
                                                <div className={cn('grid size-16 shrink-0 place-items-center overflow-hidden rounded-[1rem]', product.tint)}>
                                                    {product.id === 'jar' ? (
                                                        <div className='size-[78%]'>
                                                            <JarRender />
                                                        </div>
                                                    ) : (
                                                        <div className='aspect-[300/680] h-[80%]'>
                                                            <TubeRender details={false} />
                                                        </div>
                                                    )}
                                                </div>

                                                <div className='min-w-0 flex-1'>
                                                    <p className='text-[15px] font-medium text-cocoa'>{product.name}</p>
                                                    <p className='text-[14px] tabular-nums text-cocoa/80'>{formatPrice(product.price)}</p>
                                                </div>

                                                <div className='flex items-center gap-2'>
                                                    <button type='button' onClick={() => change(product.id, -1)} aria-label={`One less ${product.name}`} className='grid size-8 place-items-center rounded-full border border-cocoa/25 text-cocoa hover:border-cocoa focus-visible:outline-2 focus-visible:outline-cocoa'>
                                                        <Minus className='size-4' aria-hidden='true' />
                                                    </button>
                                                    <span className='w-5 text-center text-[15px] tabular-nums text-cocoa' aria-live='polite'>{bag[product.id]}</span>
                                                    <button type='button' onClick={() => change(product.id, 1)} aria-label={`One more ${product.name}`} className='grid size-8 place-items-center rounded-full border border-cocoa/25 text-cocoa hover:border-cocoa focus-visible:outline-2 focus-visible:outline-cocoa'>
                                                        <Plus className='size-4' aria-hidden='true' />
                                                    </button>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            <div className='border-t border-cocoa/10 px-6 py-6'>
                                <div className='flex items-center justify-between text-[17px] font-medium text-cocoa'>
                                    <span>Total</span>
                                    <span className='tabular-nums'>{formatPrice(total)}</span>
                                </div>

                                <p className='mt-3 rounded-[12px] bg-peach px-4 py-3 text-[14px] leading-relaxed text-cocoa'>
                                    Online checkout opens soon. Join the list and we will tell you the moment you can order.
                                </p>

                                <SmartLink href='/#join' onNavigate={() => setOpen(false)} className={buttonStyles('primary', 'lg', 'mt-4 w-full')}>
                                    Join the list
                                </SmartLink>
                            </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </Layout>
    )
}

export default Shop
